import { Calendar, CheckCircle, ArrowRight, Clock, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEffect } from 'react';

const Demo = () => {
  const demoFeatures = [
    "See Bloom in action with real data",
    "Personalized setup for your program",
    "Full platform walkthrough",
    "AI processing demonstration",
    "Q&A with our team",
    "Custom integration guidance"
  ];

  // Enhanced Calendly popup handler with click-outside-to-close
  const openCalendlyPopup = () => {
    if ((window as any).Calendly) {
      // Initialize the popup
      (window as any).Calendly.initPopupWidget({
        url: 'https://calendly.com/lucas-camargo-bloom-scouting/30min',
        parentElement: document.body,
        prefill: {},
        utm: {}
      });

      // Add custom click-outside-to-close handling after a brief delay
      setTimeout(() => {
        const calendlyModal = document.querySelector('[data-calendly-widget-popup]') || 
                              document.querySelector('.calendly-popup-wrapper') ||
                              document.querySelector('.calendly-overlay');
        
        if (calendlyModal) {
          const handleClickOutside = (event: MouseEvent) => {
            // Check if the click was on the overlay/backdrop (not on the calendar content)
            const target = event.target as HTMLElement;
            
            // Look for the actual calendar container
            const calendarContent = document.querySelector('.calendly-popup-content') ||
                                   document.querySelector('[data-calendly-widget-popup] iframe') ||
                                   calendlyModal.querySelector('iframe');
            
            // If clicked on the overlay/backdrop (but not the content), close the popup
            if (calendlyModal.contains(target) && !calendarContent?.contains(target)) {
              // Check if click was on overlay/backdrop
              if (target === calendlyModal || 
                  target.classList.contains('calendly-overlay') ||
                  target.classList.contains('calendly-popup-wrapper') ||
                  target.getAttribute('data-calendly-widget-popup') !== null) {
                
                // Close the popup
                if ((window as any).Calendly && (window as any).Calendly.closePopupWidget) {
                  (window as any).Calendly.closePopupWidget();
                } else {
                  // Fallback: hide the modal manually
                  calendlyModal.remove();
                }
              }
            }
          };

          // Add the event listener
          document.addEventListener('click', handleClickOutside, true);

          // Clean up when the popup is closed (via any method)
          const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              mutation.removedNodes.forEach((node) => {
                if (node === calendlyModal) {
                  document.removeEventListener('click', handleClickOutside, true);
                  observer.disconnect();
                }
              });
            });
          });

          observer.observe(document.body, { childList: true, subtree: true });
        }
      }, 500); // Wait for popup to fully render
    } else {
      // Fallback: open Calendly in new tab if widget not loaded
      window.open('https://calendly.com/lucas-camargo-bloom-scouting/30min', '_blank');
    }
  };

  return (
    <section id="demo" className="py-24 bg-gradient-to-br from-gray-50 via-white to-cyan-50/30 relative overflow-hidden">
      {/* Enhanced background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute top-0 left-0 w-full h-full opacity-20"
          style={{
            background: `
              radial-gradient(circle at 20% 30%, rgba(6, 182, 212, 0.08) 0%, transparent 50%),
              radial-gradient(circle at 80% 70%, rgba(20, 184, 166, 0.08) 0%, transparent 50%)
            `
          }}
        />
        
        {/* Floating gradient orbs */}
        <div className="absolute -top-20 -left-20 w-96 h-96 opacity-10">
          <div 
            className="w-full h-full rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(6, 182, 212, 0.4) 0%, transparent 70%)',
              filter: 'blur(60px)'
            }}
          />
        </div>
        
        <div className="absolute -bottom-20 -right-20 w-96 h-96 opacity-10">
          <div 
            className="w-full h-full rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(16, 185, 129, 0.4) 0%, transparent 70%)',
              filter: 'blur(60px)'
            }}
          />
        </div>

        {/* Animated particles */}
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-cyan-400/30 rounded-full animate-pulse"></div>
        <div className="absolute top-3/4 right-1/3 w-1 h-1 bg-teal-400/40 rounded-full animate-ping"></div>
        <div className="absolute top-1/2 right-1/4 w-3 h-3 bg-emerald-400/20 rounded-full animate-bounce"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            See Bloom{' '}
            <span className="bg-gradient-to-r from-cyan-600 via-teal-600 to-emerald-600 bg-clip-text text-transparent">
              In Action
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto">
            Book a personalized demo and discover how Bloom can transform your recruiting process
          </p>
        </div>

        {/* Enhanced Single Demo Card */}
        <div className="max-w-5xl mx-auto mb-16">
          <div className="relative group">
            {/* Glowing background effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 rounded-3xl blur opacity-20 group-hover:opacity-30 transition-opacity duration-500"></div>
            
            <div className="relative bg-white rounded-3xl shadow-2xl p-10 border border-gray-100 overflow-hidden transform transition-all duration-500 hover:scale-[1.01] hover:shadow-3xl">
              {/* Animated gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/50 via-white to-teal-50/50 opacity-60"></div>
              
              {/* Decorative elements */}
              <div className="absolute top-6 right-6 w-20 h-20 bg-gradient-to-br from-cyan-100 to-teal-100 rounded-full opacity-50"></div>
              <div className="absolute bottom-6 left-6 w-16 h-16 bg-gradient-to-br from-emerald-100 to-green-100 rounded-full opacity-40"></div>
              
              <div className="relative z-10">
                <div className="text-center mb-10">
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-teal-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6 shadow-lg">
                    <Sparkles className="w-4 h-4" />
                    PERSONALIZED DEMO
                  </div>
                  
                  <h3 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-4">
                    Book Your Demo
                  </h3>
                  <p className="text-xl text-gray-600 mb-4">Personalized walkthrough tailored to your program</p>
                  <div className="flex items-center justify-center gap-2 text-gray-500 bg-gray-50 px-4 py-2 rounded-full inline-flex">
                    <Clock className="w-4 h-4" />
                    <span className="font-medium">30-45 minutes</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
                  {demoFeatures.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3 group">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm group-hover:scale-110 transition-transform duration-300">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-gray-700 font-medium group-hover:text-gray-900 transition-colors duration-300">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="text-center">
                  <Button 
                    className="bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700 text-white font-bold py-4 px-12 text-lg rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 group relative overflow-hidden"
                    onClick={openCalendlyPopup}
                  >
                    {/* Button glow effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-teal-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                    
                    <span className="relative z-10 flex items-center gap-3">
                      Book Your Demo
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                    </span>
                  </Button>
                  
                  <p className="text-sm text-gray-500 mt-4 font-medium">
                    ✨ No payment required • Instant scheduling
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact info with enhanced styling */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-gray-600 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 px-6 py-3 rounded-full shadow-sm hover:shadow-md transition-shadow duration-300">
            <span className="text-sm font-medium">Have questions before booking?</span>
            <a href="mailto:lucas@bloomscouting.com,daniel@bloomscouting.com,gfmorone@berkeley.edu?subject=Demo%20Questions" className="text-cyan-600 hover:text-cyan-700 font-semibold text-sm underline underline-offset-2 transition-colors duration-300">
              Contact us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Demo;